import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, withModifiers as _withModifiers, vModelText as _vModelText, withDirectives as _withDirectives, vShow as _vShow, unref as _unref, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "mt-3 pt-3 border-t border-amber-200" }
const _hoisted_2 = { class: "font-bold text-amber-800 text-sm" }
const _hoisted_3 = { key: 0 }
const _hoisted_4 = {
  key: 0,
  class: "text-xs text-gray-500"
}
const _hoisted_5 = { class: "flex items-start justify-between gap-2" }
const _hoisted_6 = { class: "text-xs text-gray-700" }
const _hoisted_7 = { class: "font-semibold text-black" }
const _hoisted_8 = { class: "text-gray-500" }
const _hoisted_9 = ["disabled", "onClick"]
const _hoisted_10 = { class: "text-sm text-black whitespace-pre-wrap" }
const _hoisted_11 = {
  key: 2,
  class: "text-xs text-gray-500 mb-2"
}
const _hoisted_12 = { class: "mt-1 flex items-center justify-between gap-2" }
const _hoisted_13 = { class: "text-[11px] text-gray-400" }
const _hoisted_14 = ["disabled"]

import { ref, watch } from "vue";
import { fetchRest, getApiBaseUrl } from "@/config/api";
import { ConfirmModal } from "@/components";
import { useNotification } from "@/composables/useNotification";

interface BuyerComment {
  id_acheteur_commentaire: number;
  id_acheteur: number;
  id_adminuser: number;
  commentaire: string;
  date_creation: string;
  prenom?: string | null;
  nom?: string | null;
  canDelete?: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AdminBuyerComments',
  props: {
    idAcheteur: {}
  },
  setup(__props: any) {

const props = __props;

const { success, error: notifyError } = useNotification();
const comments = ref<BuyerComment[]>([]);
const expanded = ref(false);
const draft = ref("");
const loading = ref(false);
const saving = ref(false);
const pendingDelete = ref<BuyerComment | null>(null);

const authorName = (item: BuyerComment) => {
  const name = `${item.prenom || ""} ${item.nom || ""}`.trim();
  return name || "Admin";
};

const formatDateTime = (value?: string) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const load = async () => {
  if (!props.idAcheteur) {
    comments.value = [];
    return;
  }
  loading.value = true;
  try {
    const response = await fetchRest(
      `${getApiBaseUrl()}/api/admin-efp/acheteurs/commentaires/get`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idAcheteur: props.idAcheteur }),
      },
    );
    const data = await response.json();
    comments.value = Array.isArray(data) ? data : [];
  } catch {
    notifyError("Impossible de charger les commentaires.");
    comments.value = [];
  } finally {
    loading.value = false;
  }
};

const save = async () => {
  const commentaire = draft.value.trim();
  if (!commentaire || saving.value) return;
  saving.value = true;
  try {
    const response = await fetchRest(
      `${getApiBaseUrl()}/api/admin-efp/acheteurs/commentaires/save`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idAcheteur: props.idAcheteur, commentaire }),
      },
    );
    const created = await response.json();
    if (created?.id_acheteur_commentaire) {
      comments.value = [...comments.value, created];
      draft.value = "";
      success("Commentaire enregistré.");
    } else {
      notifyError("Le commentaire n'a pas été enregistré.");
    }
  } catch {
    notifyError("Impossible d'enregistrer le commentaire.");
  } finally {
    saving.value = false;
  }
};

const remove = (item: BuyerComment) => {
  if (!item.canDelete || saving.value) return;
  pendingDelete.value = item;
};

const confirmRemove = async () => {
  const item = pendingDelete.value;
  if (!item || saving.value) return;
  saving.value = true;
  try {
    const response = await fetchRest(
      `${getApiBaseUrl()}/api/admin-efp/acheteurs/commentaires/delete`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          idAcheteur: props.idAcheteur,
          idCommentaire: item.id_acheteur_commentaire,
        }),
      },
    );
    const deleted = await response.json();
    if (deleted === true) {
      comments.value = comments.value.filter(
        (comment) =>
          comment.id_acheteur_commentaire !== item.id_acheteur_commentaire,
      );
      pendingDelete.value = null;
      success("Commentaire supprimé.");
    } else {
      notifyError("Ce commentaire ne peut pas être supprimé.");
      pendingDelete.value = null;
    }
  } catch {
    notifyError("Impossible de supprimer le commentaire.");
    pendingDelete.value = null;
  } finally {
    saving.value = false;
  }
};

watch(() => props.idAcheteur, () => void load(), { immediate: true });

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("button", {
      type: "button",
      class: "flex w-full items-center justify-between gap-2 mb-2 text-left",
      onClick: _cache[0] || (_cache[0] = ($event: any) => (expanded.value = !expanded.value))
    }, [
      _createElementVNode("h4", _hoisted_2, [
        _cache[4] || (_cache[4] = _createTextVNode(" Commentaires admin")),
        (comments.value.length)
          ? (_openBlock(), _createElementBlock("span", _hoisted_3, " (" + _toDisplayString(comments.value.length) + ")", 1))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("i", {
        class: _normalizeClass([expanded.value ? 'fas fa-chevron-up' : 'fas fa-chevron-down', "text-amber-700 text-xs"])
      }, null, 2)
    ]),
    _withDirectives(_createElementVNode("div", null, [
      (loading.value)
        ? (_openBlock(), _createElementBlock("p", _hoisted_4, "Chargement..."))
        : _createCommentVNode("", true),
      (comments.value.length)
        ? (_openBlock(), _createElementBlock("ul", {
            key: 1,
            class: "space-y-2 mb-3 max-h-52 overflow-y-auto overscroll-contain",
            onWheel: _cache[1] || (_cache[1] = _withModifiers(() => {}, ["stop"]))
          }, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(comments.value, (item) => {
              return (_openBlock(), _createElementBlock("li", {
                key: item.id_acheteur_commentaire,
                class: "rounded border border-amber-100 bg-amber-50 px-2 py-1.5"
              }, [
                _createElementVNode("div", _hoisted_5, [
                  _createElementVNode("p", _hoisted_6, [
                    _createElementVNode("span", _hoisted_7, _toDisplayString(authorName(item)), 1),
                    _createElementVNode("span", _hoisted_8, " · " + _toDisplayString(formatDateTime(item.date_creation)), 1)
                  ]),
                  (item.canDelete)
                    ? (_openBlock(), _createElementBlock("button", {
                        key: 0,
                        type: "button",
                        class: "text-red-500 hover:text-red-700",
                        title: "Supprimer le commentaire",
                        disabled: saving.value,
                        onClick: ($event: any) => (remove(item))
                      }, _cache[5] || (_cache[5] = [
                        _createElementVNode("i", { class: "fas fa-trash-alt text-xs" }, null, -1)
                      ]), 8, _hoisted_9))
                    : _createCommentVNode("", true)
                ]),
                _createElementVNode("p", _hoisted_10, _toDisplayString(item.commentaire), 1)
              ]))
            }), 128))
          ], 32))
        : (!loading.value)
          ? (_openBlock(), _createElementBlock("p", _hoisted_11, " Aucun commentaire pour cet acheteur. "))
          : _createCommentVNode("", true),
      _cache[6] || (_cache[6] = _createElementVNode("label", { class: "mb-1 block text-xs font-semibold text-gray-700" }, " Ajouter un commentaire ", -1)),
      _withDirectives(_createElementVNode("textarea", {
        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((draft).value = $event)),
        rows: "2",
        maxlength: "256",
        placeholder: "Écrivez votre commentaire...",
        class: "w-full resize-none rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none"
      }, null, 512), [
        [_vModelText, draft.value]
      ]),
      _createElementVNode("div", _hoisted_12, [
        _createElementVNode("span", _hoisted_13, _toDisplayString(draft.value.trim().length) + "/256", 1),
        _createElementVNode("button", {
          type: "button",
          class: "rounded bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50",
          disabled: saving.value || !draft.value.trim(),
          onClick: save
        }, " Enregistrer ", 8, _hoisted_14)
      ])
    ], 512), [
      [_vShow, expanded.value]
    ]),
    _createVNode(_unref(ConfirmModal), {
      show: pendingDelete.value != null,
      title: "Supprimer le commentaire",
      message: "Supprimer ce commentaire ?",
      "confirm-text": "Supprimer",
      "confirm-button-class": "bg-red-600 hover:bg-red-700 focus:ring-red-500",
      "confirm-loading": saving.value,
      "loading-text": "Suppression…",
      onConfirm: confirmRemove,
      onCancel: _cache[3] || (_cache[3] = ($event: any) => (pendingDelete.value = null))
    }, null, 8, ["show", "confirm-loading"])
  ]))
}
}

})